import React, { FC, forwardRef, ReactElement, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import filterPropsByComponent from '../../../utils/components/_filterPropsByComponent';
import Control from '../Control';
import { CheckboxListControlComposedProps, CheckboxListControlDefaultProps, CheckboxListControlValidationProps } from './CheckboxListControl';

const withControl = <Value, StateValue>(WrappedComponent: FC<CheckboxListControlComposedProps<Value, StateValue>>) => {
  const WithControl = forwardRef<HTMLInputElement, CheckboxListControlComposedProps<Value, StateValue>>(({ required, children, ...props }, ref) => {
    const defaultValue = useMemo(() => [], []);

    const TypedControl = Control<Value[], StateValue[], HTMLInputElement, CheckboxListControlValidationProps>;

    return (
      <TypedControl
        defaultValue={defaultValue}
        {...filterPropsByComponent(props, TypedControl)}
        required={required}
      >
        {({
          id, value, isValid, hasWarning, hasCaution, hasInfo, isTouched, errorMessagesId, onValueChange: onValueChangeCallback,
        }) => (
          <WrappedComponent
            id={id}
            data-control
            {...props}
            value={value}
            isValid={isValid}
            hasWarning={hasWarning}
            hasCaution={hasCaution}
            hasInfo={hasInfo}
            isTouched={isTouched}
            aria-invalid={Boolean(!isValid && isTouched)}
            aria-errormessage={errorMessagesId}
            ref={ref}
            required={required}
            onValueChangeCallback={onValueChangeCallback}
          >
            {children}
          </WrappedComponent>
        )}
      </TypedControl>
    );
  }) as ((props: CheckboxListControlComposedProps<Value, StateValue>) => ReactElement<CheckboxListControlComposedProps<Value, StateValue>>) & {
    defaultProps: Partial<CheckboxListControlComposedProps<Value, StateValue>>;
  };

  WithControl.defaultProps = CheckboxListControlDefaultProps;

  return hoistNonReactStatics(WithControl, WrappedComponent);
};

export default withControl;
